import { formatInteger } from '../utils/formatters';
import type { Messages } from './de';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const en: Messages = {
  common: {
    close: 'Close',
    cancel: 'Cancel',
    removeFilter: 'Remove filter',
  },

  tiers: {
    physical: 'Investment projects',
    financial: 'Financial transactions',
  },

  investmentTypes: {
    greenfield: 'Greenfield expansion',
    brownfield: 'Brownfield expansion',
  },
  investmentStatuses: {
    announced: 'Announced',
    inProgress: 'In progress',
    finished: 'Completed',
    cancelled: 'Cancelled',
  },

  app: {
    firstPublished: 'First published: 12 June 2026',
    lastUpdate: (date: string) => ` | Last update: ${date}`,
    grouping: 'Grouping:',
    groupingNone: 'None',
    groupingCluster: 'Clusters',
    groupingSites: 'Sites',
    visualisation: 'View:',
    industries: 'Industries',
    countries: 'Countries of origin',
    timeline: 'Over time',
    drillHint: 'Click a bar to drill down into a subgroup.',
    allIndustries: 'All industries',
    selectionWahlkreis: 'Constituency',
    selectionCountry: 'Country of origin',
    selectionIndustry: 'Industry',
    disclaimer:
      'The overview and dataset of the investment projects recorded in the Investment tracker are based on data from S&P Global and on an automated query of the Google News API. Data relies on company announcements and reports of investments. The reports and data points collected in this way are scanned using an AI-assisted process and assigned to individual projects and sites. Updates follow a two-week cycle. Investments that have neither been made public nor are or were part of written, online media coverage are not recorded in the tracker. For data predating the tracker’s launch, the accuracy of the assignment was checked by random sampling; all entries recorded since the launch are additionally reviewed and verified manually once. Despite this quality assurance, we can guarantee neither the completeness nor the accuracy of the data shown. In particular, automated collection and AI-assisted assignment may lead to misclassifications, duplicates or omissions of individual projects, for example where media reports were incomplete, contradictory or subsequently corrected. Moreover, announced investments do not necessarily reflect the investment volumes actually realised; projects may be postponed, adjusted or discontinued without this being reflected in the dataset. The amounts shown are based on the public information available in each case and may differ from the actual investment totals. Although projects are shown over a longer period, recent investment projects may be over-represented in the dataset. This is due to the easier availability of current news reports and will diminish over time. The dataset is provided for information purposes only and does not constitute investment advice, an investment recommendation or any other recommendation to buy or sell financial instruments. We are continuously working to improve data quality and welcome reports of incorrect or missing entries. Users may freely use, transform and distribute this data, provided the source is correctly cited; any terms of use of the underlying data sources remain unaffected. Positions advocated using the data reflect the views of the users, not those of the publisher.',
    missingProject: 'Missing a project?',
    suggestProject: 'Suggest a project',
    contact: 'Contact:',
  },

  chips: {
    resetAllAria: 'Reset all filters',
    resetAll: '↺ Reset all',
    caption: 'Active filters:',
    clearSelection: 'Clear selection',
    period: 'Period',
    resetPeriod: 'Reset period',
    search: 'Search',
    removeSearch: 'Remove search',
    quoted: (term: string) => `“${term}”`,
    keys: {
      industries: 'Industry',
      federalStates: 'Federal state',
      investmentStatuses: 'Status',
      companies: 'Company',
    },
  },

  filterPanel: {
    filter: 'Filters',
    closeFilter: 'Close filters',
    industry: 'Industry',
    allIndustries: 'All industries',
    federalState: 'Federal state',
    allFederalStates: 'All federal states',
    status: 'Status',
    allStatuses: 'All statuses',
    company: 'Company',
    allCompanies: 'All companies',
    reset: 'Reset',
    done: 'Done',
  },

  select: {
    selected: (n: number) => `${n} selected`,
    search: 'Search...',
    resultsOf: (shown: number, total: number) => `${shown} of ${total} results`,
    more: (n: number) => `${n} more — refine your search`,
    noResults: 'No results',
    noResultsFor: (term: string) => `No results for "${term}"`,
  },

  timeRange: {
    label: 'Period',
    from: 'Period from',
    to: 'Period to',
    months12: '12 months',
    years3: '3 years',
    all: 'All',
  },

  dateField: {
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    chooseDate: 'Choose a date',
    year: 'Year',
    openCalendar: (field: string) => `${field}: open calendar`,
  },

  search: {
    placeholder: 'Search projects…',
    aria: 'Search projects',
    clear: 'Clear search',
  },

  charts: {
    count: 'Count',
    volume: 'Volume',
    exportImage: 'Export chart as image',
    breadcrumbAria: 'Industry drill-down',
    noData: 'No data',
    total: 'Total',
    axisUnit: { bn: '€ bn', m: '€ m', k: '€ k', one: '€' },
  },

  table: {
    entries: (n: number) => `${formatInteger(n)} ${plural(n, 'entry', 'entries')}`,
    exportCsv: 'Export as CSV',
    mapSelection: 'Map selection active —',
    investments: (n: number) => `${formatInteger(n)} ${plural(n, 'investment', 'investments')}`,
    clearFilter: 'Clear filter',
    company: 'Company',
    project: 'Project',
    industry: 'Industry',
    type: 'Investment type',
    typeShort: 'Type',
    volume: 'Volume',
    firstDate: 'First reported',
    lastUpdate: 'Last update',
    projectStart: 'Project start',
    projectEnd: 'Project end',
    location: 'Location',
    status: 'Status',
    range: (from: number, to: number, total: number) => `${from}–${to} of ${formatInteger(total)}`,
    previous: '‹ Previous',
    next: 'Next ›',
  },

  modal: {
    loadingHistory: 'Loading history...',
    history: (n: number) => `History (${n} ${plural(n, 'entry', 'entries')})`,
    source: 'Source:',
    errorInEntry: 'Spotted an error in this entry?',
    suggestCorrection: 'Suggest a correction',
  },

  news: {
    label: 'Latest news',
    labelShort: 'Latest',
  },

  spotlights: {
    title: 'Spotlights',
  },

  map: {
    exitFullscreen: 'Exit full screen',
    enterFullscreen: 'Full-screen map',
    exportImage: 'Export map as image',
    typeLegend: 'Investment type',
    volumeLegend: 'Investment volume',
    onlyThisType: 'Click: show only this type',
    investments: (n: number) => `${n} ${plural(n, 'investment', 'investments')}`,
    compactVolume: {
      bn: (n: string) => `€ ${n} bn`,
      m: (n: string) => `€ ${n} m`,
      k: (n: string) => `€ ${n} k`,
    },
    exporting: 'Exporting map …',
    twoFingers: 'Use two fingers to move the map',
  },

  exports: {
    source: 'Source: dezernatzukunft.org/daten/investitionstracker',
    chartFilePrefix: 'investments',
    mapFileName: 'investment_map',
    csvFileName: (date: string) => `investment_projects_export_${date}.csv`,
    csvHeaders: {
      company: 'Company',
      project: 'Project',
      industry: 'Industry',
      type: 'Investment_type',
      volume: 'Volume_EUR',
      jobs: 'Jobs',
      firstDate: 'First_reported',
      lastUpdate: 'Last_update',
      projectStart: 'Project_start',
      projectEnd: 'Project_end',
      location: 'Location',
      status: 'Status',
    },
  },

  exportDialog: {
    title: 'Export data',
    loading: 'Loading data...',
    building: 'Building CSV...',
    failed: 'Export failed',
    currentView: 'Current view',
    currentViewDescription: (n: number) =>
      `${formatInteger(n)} ${plural(n, 'entry', 'entries')} with the current filters`,
    allData: 'All data',
    allDataDescription: 'Export all investment projects without filters',
    export: 'Export',
  },

  feedback: {
    titleCorrection: 'Suggest a correction',
    titleNewProject: 'Suggest a project',
    errors: {
      company: 'Please enter the company.',
      city: 'Please enter the city.',
      cityPick: 'Please choose the city from the list of suggestions so that we can place the project correctly on the map.',
      sourceCorrection: 'Please provide a link to a source that supports your correction.',
      source: 'Please provide a link to the source.',
      sourceUrl: 'The source link must start with http:// or https://.',
      email: 'Please enter a valid email address (or leave the field empty).',
      volume: 'Please enter the investment volume as a number in € million.',
      jobs: 'Please enter the number of jobs as a whole number.',
      init: 'The form could not be initialised. Please close the dialog and try again.',
    },
    sending: 'Sending your report…',
    thanks: 'Thank you for your report!',
    reviewNote: 'We review every report manually, usually within a few days.',
    sendFailed: 'Sending failed. Please try again later or email us at',
    backToForm: 'Back to the form',
    introCorrection: 'Spotted an error in this entry? Simply correct the fields concerned — we review every report manually before any data is changed.',
    introNewProject: 'Is an investment project missing from our overview? Suggest it here — we review every suggestion manually before it is added.',
    company: 'Company *',
    companyPlaceholder: 'e.g. Example GmbH',
    project: 'Project name (optional)',
    projectPlaceholder: 'e.g. New battery plant',
    cityRequired: 'City *',
    city: 'City',
    cityPlaceholder: 'e.g. Leipzig — please pick from the list',
    cityRecognised: (place: string) => `✓ City recognised: ${place} — it will be placed on the map.`,
    cityLookupDown: 'City search is currently unavailable — the city will be matched during review.',
    multipleLocations: 'This entry has several locations; the first one is shown here.',
    federalState: 'Federal state (optional)',
    pleaseChoose: 'Please choose…',
    type: 'Type of investment (optional)',
    status: 'Status (optional)',
    volume: 'Investment volume in € million (optional)',
    volumePlaceholder: 'e.g. 250',
    jobs: 'New jobs (optional)',
    jobsPlaceholder: 'e.g. 500',
    date: 'Date of announcement (optional)',
    source: 'Link to source *',
    sourceHintCorrection: 'Required: public evidence for your correction, e.g. a press release or newspaper article.',
    sourceHint: 'Required: public evidence, e.g. a press release or newspaper article.',
    explanation: 'Explanation (optional)',
    description: 'Description (optional)',
    messagePlaceholderCorrection: 'What did you correct, and how do you know?',
    messagePlaceholder: 'Further details about the project…',
    email: 'Email (optional)',
    emailPlaceholder: 'your@email.com',
    emailHint: 'Only for follow-up questions. You will not receive any automated emails.',
    submit: 'Submit',
    types: {
      greenfield: 'New build (greenfield)',
      brownfield: 'Expansion of existing site (brownfield)',
    },
    statuses: {
      announced: 'Announced',
      inProgress: 'In progress',
      finished: 'Completed',
      cancelled: 'Cancelled',
    },
  },

  city: {
    searching: 'Searching…',
    unavailable: 'City search unavailable — you can also enter the city as free text.',
    noHits: 'No matches.',
  },

  dataset: {
    name: 'Investment tracker',
    description:
      'Announced investment projects by private companies in Germany — '
      + 'by industry, federal state, investment type and volume. '
      + 'Freely searchable, continuously updated.',
    country: 'Germany',
    variables: [
      'Investment volume',
      'Jobs created',
      'Investment type',
      'Project status',
      'Industry',
      'Location',
    ],
  },
};
